import { useCallback, useEffect, useMemo, useState } from 'react';

import { DEFAULT_SITE_CONTENT } from '@/constants/site-content.defaults';
import { resetPublicSiteContentCache } from '@/hooks/useSiteContent';
import { siteContentService } from '@/services/site-content.service';
import type { SiteContent } from '@/types/site-content';
import { classNames } from '@/utils/classNames';

import styles from './AdminSiteContentSection.module.css';

function serializeAdminContentState(content: SiteContent): string {
  return JSON.stringify(content);
}

export const AdminSiteContentSection = () => {
  const [draft, setDraft] = useState<SiteContent | null>(null);
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [announceSaveToken, setAnnounceSaveToken] = useState(0);

  useEffect(() => {
    void siteContentService
      .getAdmin()
      .then((content) => {
        setDraft(content);
        setSavedFingerprint(serializeAdminContentState(content));
        setLoadError(null);
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Failed to load site content');
      });
  }, []);

  const draftFingerprint = useMemo(() => (draft !== null ? serializeAdminContentState(draft) : ''), [draft]);

  const hasUnsavedChanges = savedFingerprint !== null && draft !== null && draftFingerprint !== savedFingerprint;

  useEffect(() => {
    if (announceSaveToken === 0) {
      return;
    }

    const t = window.setTimeout(() => {
      setAnnounceSaveToken(0);
    }, 4500);

    return () => {
      window.clearTimeout(t);
    };
  }, [announceSaveToken]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(async () => {
    if (!draft) return;

    setSaveError(null);
    setIsSaving(true);
    try {
      const updatedContent = await siteContentService.patchAdmin(draft);
      setDraft(updatedContent);
      setSavedFingerprint(serializeAdminContentState(updatedContent));
      setAnnounceSaveToken(Date.now());
      resetPublicSiteContentCache();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  }, [draft]);

  if (loadError && !draft) {
    return (
      <div className={styles.wrap}>
        <p className={styles.error}>{loadError}</p>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => {
            window.location.reload();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!draft) {
    return <p className={styles.muted}>Loading site content…</p>;
  }

  return (
    <div className={styles.wrap}>
      {(hasUnsavedChanges || announceSaveToken !== 0) && (
        <div className={styles.alertDock}>
          <div className={styles.alertDockInner}>
            {hasUnsavedChanges ? (
              <>
                <p className={styles.unsavedNote}>
                  You have unsaved changes. Save before leaving if you want to keep them.
                </p>
                <button
                  type="button"
                  className={classNames(styles.primaryBtn, styles.dockPrimaryBtn)}
                  disabled={isSaving || !hasUnsavedChanges}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {isSaving ? 'Saving…' : 'Save changes'}
                </button>
              </>
            ) : (
              <p className={styles.savedNote} role="status" aria-live="polite">
                Changes saved successfully.
              </p>
            )}
          </div>
        </div>
      )}
      <h2 className={styles.heading}>Marketing content</h2>
      <p className={styles.lead}>Hero, FAQ, and footer copy served on the home page and legal pages.</p>

      <section className={styles.block}>
        <h3 className={styles.blockTitle}>Hero</h3>
        <label className={styles.label}>
          Title above the booking form
          <input
            className={styles.input}
            value={draft.heroTitle}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((prev) => (prev ? { ...prev, heroTitle: v } : prev));
            }}
          />
        </label>
      </section>

      <section className={styles.block}>
        <h3 className={styles.blockTitle}>FAQ</h3>
        {draft.faqItems.map((faq, index) => (
          <fieldset className={styles.fieldset} key={faq.id}>
            <legend className={styles.legend}>FAQ {String(index + 1)}</legend>
            <label className={styles.label}>
              Question
              <input
                className={styles.input}
                value={faq.question}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((prev) => {
                    if (!prev) return prev;
                    const next = [...prev.faqItems];
                    const cur = next[index];
                    if (!cur) return prev;
                    next[index] = { id: cur.id, question: v, answer: cur.answer };
                    return { ...prev, faqItems: next };
                  });
                }}
              />
            </label>
            <label className={styles.label}>
              Answer
              <textarea
                className={styles.textarea}
                rows={4}
                value={faq.answer}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((prev) => {
                    if (!prev) return prev;
                    const next = [...prev.faqItems];
                    const cur = next[index];
                    if (!cur) return prev;
                    next[index] = { id: cur.id, question: cur.question, answer: v };
                    return { ...prev, faqItems: next };
                  });
                }}
              />
            </label>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={() => {
                setDraft((prev) => {
                  if (!prev) return prev;
                  if (prev.faqItems.length <= 1) return prev;
                  return {
                    ...prev,
                    faqItems: prev.faqItems.filter((_, i) => i !== index),
                  };
                });
              }}
            >
              Remove
            </button>
          </fieldset>
        ))}
        <div className={styles.sectionEndActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => {
              setDraft((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  faqItems: [...prev.faqItems, { id: crypto.randomUUID(), question: '', answer: '' }],
                };
              });
            }}
          >
            Add FAQ
          </button>
        </div>
      </section>

      <section className={styles.block}>
        <h3 className={styles.blockTitle}>Footer</h3>
        <label className={styles.label}>
          Company display name
          <input
            className={styles.input}
            value={draft.footer.companyDisplayName}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((prev) =>
                prev ? { ...prev, footer: { ...prev.footer, companyDisplayName: v } } : prev,
              );
            }}
          />
        </label>
        <label className={styles.label}>
          Location & contact (plain text, multiple lines)
          <textarea
            className={styles.textarea}
            rows={12}
            value={draft.footer.locationBlock}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((prev) =>
                prev ? { ...prev, footer: { ...prev.footer, locationBlock: v } } : prev,
              );
            }}
          />
        </label>

        <span className={styles.linkSectionsLabel}>Link sections</span>

        {draft.footer.linkSections.map((section, sIndex) => (
          <fieldset className={styles.fieldset} key={`section-${String(sIndex)}`}>
            <legend className={styles.legend}>{`Section ${String(sIndex + 1)}`}</legend>
            <label className={styles.label}>
              Column title
              <input
                className={styles.input}
                value={section.title}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((prev) => {
                    if (!prev) return prev;
                    const next = [...prev.footer.linkSections];
                    const sec = next[sIndex];
                    if (!sec) return prev;
                    next[sIndex] = { title: v, links: [...sec.links] };
                    return { ...prev, footer: { ...prev.footer, linkSections: next } };
                  });
                }}
              />
            </label>
            {section.links.map((link, lIndex) => (
              <div className={styles.linkRow} key={`${String(sIndex)}-${String(lIndex)}-${link.href}`}>
                <label className={styles.label}>
                  Label
                  <input
                    className={styles.input}
                    value={link.label}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDraft((prev) => {
                        if (!prev) return prev;
                        const sections = [...prev.footer.linkSections];
                        const sect = sections[sIndex];
                        if (!sect) return prev;
                        const links = [...sect.links];
                        const lk = links[lIndex];
                        if (!lk) return prev;
                        links[lIndex] = { label: v, href: lk.href };
                        sections[sIndex] = { title: sect.title, links };
                        return { ...prev, footer: { ...prev.footer, linkSections: sections } };
                      });
                    }}
                  />
                </label>
                <label className={styles.label}>
                  URL
                  <input
                    className={styles.input}
                    value={link.href}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDraft((prev) => {
                        if (!prev) return prev;
                        const sections = [...prev.footer.linkSections];
                        const sect = sections[sIndex];
                        if (!sect) return prev;
                        const links = [...sect.links];
                        const lk = links[lIndex];
                        if (!lk) return prev;
                        links[lIndex] = { label: lk.label, href: v };
                        sections[sIndex] = { title: sect.title, links };
                        return { ...prev, footer: { ...prev.footer, linkSections: sections } };
                      });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={styles.dangerBtn}
                  onClick={() => {
                    setDraft((prev) => {
                      if (!prev) return prev;
                      const sections = [...prev.footer.linkSections];
                      const sect = sections[sIndex];
                      if (!sect) return prev;
                      const links = sect.links.filter((_, i) => i !== lIndex);
                      if (links.length === 0) return prev;
                      sections[sIndex] = { title: sect.title, links };
                      return { ...prev, footer: { ...prev.footer, linkSections: sections } };
                    });
                  }}
                >
                  Remove link
                </button>
              </div>
            ))}
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setDraft((prev) => {
                  if (!prev) return prev;
                  const sections = [...prev.footer.linkSections];
                  const sect = sections[sIndex];
                  if (!sect) return prev;
                  sections[sIndex] = {
                    title: sect.title,
                    links: [...sect.links, { label: 'Link', href: '/' }],
                  };
                  return { ...prev, footer: { ...prev.footer, linkSections: sections } };
                });
              }}
            >
              Add link
            </button>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={() => {
                setDraft((prev) => {
                  if (!prev) return prev;
                  if (prev.footer.linkSections.length <= 1) return prev;
                  return {
                    ...prev,
                    footer: {
                      ...prev.footer,
                      linkSections: prev.footer.linkSections.filter((_, i) => i !== sIndex),
                    },
                  };
                });
              }}
            >
              Remove section
            </button>
          </fieldset>
        ))}
        <div className={styles.sectionEndActions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => {
              setDraft((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  footer: {
                    ...prev.footer,
                    linkSections: [
                      ...prev.footer.linkSections,
                      { title: 'New section', links: [{ label: 'Link', href: '/' }] },
                    ],
                  },
                };
              });
            }}
          >
            Add section
          </button>
        </div>
      </section>

      {saveError ? <p className={styles.error}>{saveError}</p> : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primaryBtn}
          disabled={isSaving || !hasUnsavedChanges}
          onClick={() => {
            void handleSave();
          }}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() => {
            setDraft(structuredClone(DEFAULT_SITE_CONTENT));
          }}
        >
          Reset form to baked-in defaults (not saved until you click Save)
        </button>
      </div>
    </div>
  );
};
